import { matchPath } from "react-router-dom";

import type { EventMap, PortalApp } from "./analytics";

/**
 * Route → human page name, for `Page Viewed`. Kept beside the event map so it's
 * obvious when a route in src/routes/index.tsx has no entry: unmatched paths
 * report as "Not Found", the same page the router renders for them.
 *
 * Order matters only where patterns overlap; react-router's `matchPath` is
 * exact, so none currently do.
 */
const PAGES: Array<{ pattern: string; page: string; app: PortalApp }> = [
  { pattern: "/", page: "Dashboard", app: "dashboard" },
  { pattern: "/debug", page: "Debug", app: "debug" },
  { pattern: "/liberty-ai", page: "LibertyAI Chat", app: "liberty-ai" },
  {
    pattern: "/board/conferences",
    page: "Board Conferences",
    app: "board",
  },
  {
    pattern: "/board/conferences/:conferenceId",
    page: "Board Conference Details",
    app: "board",
  },
  { pattern: "/board/documents", page: "Board Documents", app: "board" },
  {
    pattern: "/honorarium/conferences",
    page: "Honorarium Conferences",
    app: "honorarium",
  },
  {
    pattern: "/honorarium/conferences/:conferenceId",
    page: "Honorarium Conference Details",
    app: "honorarium",
  },
  { pattern: "/people/referrals", page: "People Referrals", app: "people" },
  { pattern: "/people/bio/:personId", page: "Person Bio", app: "people" },
  {
    pattern: "/tax-forms/conferences",
    page: "Tax Forms Conferences",
    app: "tax-forms",
  },
  {
    pattern: "/tax-forms/conferences/:conferenceId/:confereeId",
    page: "Tax Forms Conference Registration",
    app: "tax-forms",
  },
  {
    pattern: "/tax-forms/view/:confereeId/:formName",
    page: "Tax Form Viewer",
    app: "tax-forms",
  },
];

/**
 * Routes that render nothing a user looks at. `/callback` is the Auth0
 * redirect target, which navigates on to the requested route immediately.
 */
const UNTRACKED_PATHS = new Set(["/callback"]);

export function isTrackedPath(pathname: string): boolean {
  return !UNTRACKED_PATHS.has(pathname);
}

type PageInfo = Omit<EventMap["Page Viewed"], "previous_page">;

/**
 * Resolves a pathname to its page name, sub-app, route template and the ids
 * in its dynamic segments. Ids are Salesforce/eNumber identifiers, not
 * personal data, and are what make "which conference did they look at"
 * answerable in Mixpanel.
 */
export function getPageInfo(pathname: string): PageInfo {
  for (const { pattern, page, app } of PAGES) {
    const match = matchPath(pattern, pathname);
    if (!match) continue;

    const { conferenceId, confereeId, personId, formName } = match.params;
    return {
      page,
      app,
      path_template: pattern,
      ...(conferenceId && { conference_id: conferenceId }),
      ...(confereeId && { conferee_id: confereeId }),
      ...(personId && { person_id: personId }),
      ...(formName && { form_name: formName }),
    };
  }

  return { page: "Not Found", app: "unknown", path_template: "*" };
}

/** Page name only, for click events that record where they happened. */
export function getPageName(pathname: string): string {
  return getPageInfo(pathname).page;
}
